import React, { useEffect, useMemo, useState } from 'react';

import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Home } from '@mui/icons-material';
import { Spinner } from 'react-bootstrap';
import { FaCheck, FaSearch } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';

import { getDmsDriver, listActivitiesByDriver, listAllDmsDrivers } from './operations';
import { PORTAL_STATUS, clientLabel, downloadCsv, driverLabel } from './utils';
import { Breadcrumbs, ExportCsvButton, Footer, SubHeader } from '../../components';
import { formatIsoToDate } from '../../utils';

import './styles.scoped.css';

const latestDate = dates => dates.filter(Boolean).sort().pop() || null;

const SoloDriver = () => {
  const navigate = useNavigate();
  const { driverId } = useParams();
  const [driver, setDriver] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openClients, setOpenClients] = useState({});
  const [searchText, setSearchText] = useState('');
  const [allDrivers, setAllDrivers] = useState(null);

  useEffect(() => {
    setLoading(true);
    setOpenClients({});
    setSearchText('');
    Promise.all([getDmsDriver(driverId), listActivitiesByDriver(Number(driverId))])
      .then(([driverResponse, activityResponse]) => {
        setDriver(driverResponse);
        setActivities(activityResponse);
        setLoadError('');
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [driverId]);

  // Parent rows: one per client, ordered by most recent contract activity end date (UC 4.1)
  const clientGroups = useMemo(() => {
    const byClient = new Map();
    activities.forEach(activity => {
      const key = activity.clientId ?? activity.clientName;
      if (!byClient.has(key)) byClient.set(key, []);
      byClient.get(key).push(activity);
    });
    return [...byClient.entries()]
      .map(([key, clientActivities]) => ({
        key: String(key),
        clientName: clientActivities[0].clientName,
        clientId: clientActivities[0].clientId,
        endDate: latestDate(clientActivities.map(a => a.contractEndDate)),
        startBenSentDate: latestDate(clientActivities.map(a => a.startBenSentDate)),
        termBenSentDate: latestDate(clientActivities.map(a => a.termBenSentDate)),
        serviceAction: clientActivities.some(a => !a.clearServiceActionFlag),
        benefitsAction: clientActivities.some(a => !a.clearBenefitActionFlag),
        // Child rows: oldest to newest by start date (UC 4.2)
        activities: [...clientActivities].sort((a, b) =>
          (a.contractStartDate || '').localeCompare(b.contractStartDate || ''),
        ),
      }))
      .sort((a, b) => (b.endDate || '').localeCompare(a.endDate || ''));
  }, [activities]);

  // Driver / Candidate ID search: jump to another driver's page (UC 4.1)
  const suggestions = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    if (!search || !allDrivers) return [];
    return allDrivers.filter(d => d.id !== driverId && driverLabel(d).toLowerCase().includes(search)).slice(0, 8);
  }, [allDrivers, driverId, searchText]);

  const handleSearchChange = event => {
    setSearchText(event.target.value);
    if (allDrivers === null) {
      setAllDrivers([]);
      listAllDmsDrivers()
        .then(setAllDrivers)
        .catch(() => setAllDrivers([]));
    }
  };

  const handleExport = () =>
    downloadCsv(
      `dms-driver-${driverId}.csv`,
      [
        'Client Name & Number',
        'Contract Activity ID',
        'Start Date',
        'End Date',
        'For Pay',
        'Hire',
        'DNS',
        'Term',
        'Start Ben. Sent Date',
        'Term Ben. Sent Date',
      ],
      clientGroups.flatMap(group =>
        group.activities.map(activity => [
          clientLabel(group.clientName, group.clientId),
          activity.id,
          formatIsoToDate(activity.contractStartDate),
          formatIsoToDate(activity.contractEndDate),
          activity.forPayroll ? 'Yes' : '',
          activity.portalStatus === PORTAL_STATUS.HIRE_SEND ? 'Yes' : '',
          activity.portalStatus === PORTAL_STATUS.HIRE_DNS ? 'Yes' : '',
          activity.portalStatus === PORTAL_STATUS.TERM_SEND ? 'Yes' : '',
          formatIsoToDate(activity.startBenSentDate),
          formatIsoToDate(activity.termBenSentDate),
        ]),
      ),
    );

  const renderChildRow = (activity, index) => (
    <tr key={activity.id} className={index % 2 === 0 ? 'child-row-even' : 'child-row-odd'}>
      <td />
      <td />
      <td>{activity.id}</td>
      <td>{formatIsoToDate(activity.contractStartDate)}</td>
      <td>{formatIsoToDate(activity.contractEndDate)}</td>
      <td className="centered">{activity.forPayroll && <FaCheck className="for-pay-check" aria-label="For Pay" />}</td>
      <td className="centered">
        <input type="radio" disabled readOnly checked={activity.portalStatus === PORTAL_STATUS.HIRE_SEND} />
      </td>
      <td className="centered">
        <input type="radio" disabled readOnly checked={activity.portalStatus === PORTAL_STATUS.HIRE_DNS} />
      </td>
      <td className="centered">
        <input type="radio" disabled readOnly checked={activity.portalStatus === PORTAL_STATUS.TERM_SEND} />
      </td>
      <td>{formatIsoToDate(activity.startBenSentDate)}</td>
      <td>{formatIsoToDate(activity.termBenSentDate)}</td>
      <td className="centered">
        {!activity.clearServiceActionFlag && <span className="action-badge service" title="Service Action" />}
      </td>
      <td className="centered">
        {!activity.clearBenefitActionFlag && <span className="action-badge benefits" title="Benefits Action" />}
      </td>
      <td className="centered">
        <button
          type="button"
          className="icon-button"
          aria-label={`Edit contract activity ${activity.id}`}
          onClick={() => navigate(`/dms-driver-log/contract-activity/${activity.id}`)}>
          <MdEdit className="edit-icon" />
        </button>
      </td>
    </tr>
  );

  return (
    <>
      <SubHeader
        text="DMS Driver Log"
        buttons={[{ icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
      />
      <Breadcrumbs leftTitle="DMS Driver Log" rightTitle={driver ? driverLabel(driver) : ''} route="/dms-driver-log" />
      <div className="wrap">
        <div className="export-row">
          <ExportCsvButton onClick={handleExport} />
        </div>
        <div className="list-controls">
          <div>
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                aria-label="Search by driver name or candidate ID"
                value={searchText}
                onChange={handleSearchChange}
              />
            </div>
            {suggestions.map(suggestion => (
              <div
                key={suggestion.id}
                className="search-suggestion"
                onClick={() => navigate(`/dms-driver-log/driver/${suggestion.id}`)}>
                {driverLabel(suggestion)}
              </div>
            ))}
          </div>
        </div>
        {loadError && <div className="field-error">{loadError}</div>}
        {loading ? (
          <Spinner animation="border" style={{ margin: '40px auto', color: 'var(--primary)' }} />
        ) : (
          <table className="driver-table">
            <thead>
              <tr>
                <th />
                <th>Client Name & Number</th>
                <th>Contract Activity ID</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>For Pay</th>
                <th>Hire</th>
                <th>DNS</th>
                <th>Term</th>
                <th>Start Ben. Sent Date</th>
                <th>Term Ben. Sent Date</th>
                <th>Service Action</th>
                <th>Benefits Action</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {clientGroups.flatMap(group => {
                const isOpen = !!openClients[group.key];
                const parentRow = (
                  <tr key={group.key}>
                    <td className="centered">
                      <button
                        type="button"
                        className="chevron-button"
                        aria-label={`${isOpen ? 'Hide' : 'Show'} contract activities for ${group.clientName}`}
                        aria-expanded={isOpen}
                        onClick={() => setOpenClients(prev => ({ ...prev, [group.key]: !isOpen }))}>
                        <FontAwesomeIcon icon={faChevronDown} className={`chevron-icon ${isOpen ? 'open' : ''}`} />
                      </button>
                    </td>
                    <td>{clientLabel(group.clientName, group.clientId)}</td>
                    <td />
                    <td />
                    <td>{formatIsoToDate(group.endDate)}</td>
                    <td />
                    <td />
                    <td />
                    <td />
                    <td>{formatIsoToDate(group.startBenSentDate)}</td>
                    <td>{formatIsoToDate(group.termBenSentDate)}</td>
                    <td className="centered">
                      {group.serviceAction && <span className="action-badge service" title="Service Action" />}
                    </td>
                    <td className="centered">
                      {group.benefitsAction && <span className="action-badge benefits" title="Benefits Action" />}
                    </td>
                    <td />
                  </tr>
                );
                return [parentRow, ...(isOpen ? group.activities.map(renderChildRow) : [])];
              })}
            </tbody>
          </table>
        )}
      </div>
      <Footer />
    </>
  );
};

export default SoloDriver;
